import { useState, useCallback } from 'react';
import { ScheduledCourse } from '@/app/types/course';
import { googleCalendarService } from '@/app/services/googleCalendar';
import { useAuth } from '@/app/Components/AuthProvider';

interface ExportResult {
  success: boolean;
  message: string;
  eventCount: number;
}

export const useGoogleCalendar = () => {
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [lastExportResult, setLastExportResult] = useState<ExportResult | null>(null);

  const exportToGoogleCalendar = useCallback(async (courses: ScheduledCourse[], scheduleName?: string): Promise<ExportResult> => {
    if (!user) {
      return {
        success: false,
        message: 'Please sign in to export to Google Calendar',
        eventCount: 0
      };
    }

    if (courses.length === 0) {
      return {
        success: false,
        message: 'No courses to export',
        eventCount: 0
      };
    }

    setIsExporting(true);
    setLastExportResult(null);

    try {
      // Check if user has Google Calendar access
      const hasAccess = await googleCalendarService.checkGoogleCalendarAccess();
      /* if (!hasAccess) {
        return {
          success: false,
          message: 'Please sign in with Google to export to calendar',
          eventCount: 0
        };
      } */

      // Export schedule to Google Calendar
      const result = await googleCalendarService.exportScheduleToGoogleCalendar(courses, scheduleName);
      setLastExportResult(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export to Google Calendar';
      const result = {
        success: false,
        message: errorMessage,
        eventCount: 0
      };
      setLastExportResult(result);
      return result;
    } finally {
      setIsExporting(false);
    }
  }, [user]);

  const clearExportResult = useCallback(() => {
    setLastExportResult(null);
  }, []);

  return {
    exportToGoogleCalendar,
    isExporting,
    lastExportResult,
    clearExportResult
  };
}; 